import type { Person, TokenPayload } from '@local/common-lib';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { CodedError } from '../error.js';

export interface MaybeRawToken {
  token?: string | null;
}

export namespace TokenType {
  export const ACCESS = 'access';
  export const REFRESH = 'refresh';
}

export namespace TokenSubjectType {
  export const USER = 'user';
}

export interface TokenProviderLogger {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  error: (...args: any[]) => void;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  verbose: (...args: any[]) => void;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  warn: (...args: any[]) => void;
}

export interface TokenProviderOptions {
  logger?: TokenProviderLogger;
  retryCount?: number;
  setAuth: (authenticationState?: AuthenticationState) => void;
  thresholdBeforeEarlyRefresh?: number;
  thresholdBeforeExpire: number;
}

export interface Token {
  expires: number;
  payload: TokenPayload;
  raw: string;
  refreshExpires?: number;
}

export interface AxiosOptions {
  config?: AxiosRequestConfig;
}

export interface Auth {
  refresh: () => Promise<unknown>;
  signIn: (
    email: string,
    password: string,
    remember: boolean,
    sharedDevice: boolean,
  ) => Promise<boolean>;
  signOut: () => Promise<void>;
  state: AuthenticationState | undefined;
  switchOrganization: (organizationId: string) => Promise<void>;
}

export interface WebOpenIdConfiguration {
  authorization_endpoint: string;
  end_session_endpoint: string;
  token_endpoint: string;
}

export interface OpenIdConfiguration {
  authUrl: string;
  signOutUrl: string;
  tokenEndpoint: string;
}

export enum AuthStatus {
  AUTHENTICATED = 0,
  CONFIG_ERROR = 1,
  AUTHENTICATION_FAILED = 2,
  NOT_AUTHENTICATED = 3,
}

export interface UserInfo {
  /** Organization roles only - super admin is `isSuperAdmin`, never a role. */
  hasRole: (...anyOfRole: Person.Role[]) => boolean;
  /** Super admin is a claim of its own, never a role. */
  isSuperAdmin: boolean;
  /** The organization this session acts in. Absent for a super admin. */
  organizationId?: string;
  userId: string;
}

export type AuthenticationState =
  | AuthenticatedState
  | ConfigErrorState
  | AuthenticationFailedState
  | NotAuthenticatedState;

export interface AuthenticatedState {
  axios: AxiosInstance;
  error?: CodedError;
  loading: boolean;
  refreshExpires?: number;
  status: AuthStatus.AUTHENTICATED;
  timestamp: number;
  userInfo: UserInfo;
}

export interface ConfigErrorState {
  error: Error;
  status: AuthStatus.CONFIG_ERROR;
}

export interface AuthenticationFailedState {
  failure: AuthenticationFailure;
  status: AuthStatus.AUTHENTICATION_FAILED;
}

export interface AuthenticationFailure {
  error: string;
  errorDescription: string;
}

export interface NotAuthenticatedState {
  error?: CodedError;
  loading: boolean;
  status: AuthStatus.NOT_AUTHENTICATED;
}
